import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Menu } from 'antd';
import { HiLogout } from 'react-icons/hi';
import { SidebarGroups, SidebarLink } from '../../Utils/Sideber/SidebarLink';
import { baseApi, clearAuth } from '../../Redux/BaseUrl';
import { useLogoutMutation } from '../../Redux/Apis/authApi';
import { usePermissions } from './Can';

const Sidebar = ({ collapsed, onNavigate }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const [logout] = useLogoutMutation();
    const [openKeys, setOpenKeys] = useState([]);

    /* Only show items the admin is permitted to use; a group is hidden when none
       of its links are visible. */
    const links = SidebarLink.filter((item) => can(item.permission));
    const groups = SidebarGroups
        .map((group) => ({ ...group, links: group.links.filter((link) => can(link.permission)) }))
        .filter((group) => group.links.length > 0);

    const items = useMemo(() => [
        ...links.map((l) => ({ key: l.path, icon: l.icon, label: l.label })),
        ...groups.map((g) => ({
            key: g.label,
            icon: g.icon,
            label: g.label,
            children: g.links.map((c) => ({ key: c.path, label: c.label })),
        })),
    ], [links, groups]);

    /* Auto-expand the group that owns the active route as the path changes. */
    useEffect(() => {
        const active = groups.find((g) => g.links.some((l) => l.path === location.pathname));
        if (active) setOpenKeys((keys) => (keys.includes(active.label) ? keys : [...keys, active.label]));
    }, [location.pathname]);

    /* Clear the cookie server-side, scrub legacy storage, then RESET the RTK
       Query cache so the stale (still-ADMIN) profile data can't make the login
       guard redirect back into the app — which otherwise ping-pongs infinitely. */
    const handleLogout = async () => {
        try { await logout().unwrap() } catch { /* log out client-side regardless */ }
        clearAuth();
        dispatch(baseApi.util.resetApiState());
        navigate('/login');
    };

    return (
        <div className='flex flex-col h-screen text-[var(--bg-white)]'>
            <div className={`shrink-0 h-[72px] flex items-center px-4 ${collapsed ? 'justify-center' : ''}`}>
                <div className='flex items-center gap-3 overflow-hidden'>
                    <img
                        src='/favicon.ico'
                        alt='ilera'
                        className='w-10 h-10 shrink-0 rounded-lg object-cover border border-[var(--bg-white)]/15'
                    />
                    {!collapsed && (
                        <div className='leading-tight overflow-hidden'>
                            <p className='font-semibold text-[var(--bg-white)] truncate'>ilera</p>
                            <p className='text-xs text-[var(--bg-white)]/60 truncate'>Admin Portal</p>
                        </div>
                    )}
                </div>
            </div>
            <div className='flex-1 min-h-0 overflow-y-auto py-2'>
                <Menu
                    theme='dark'
                    mode='inline'
                    items={items}
                    inlineCollapsed={collapsed}
                    selectedKeys={[location.pathname]}
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    onClick={({ key }) => { navigate(key); onNavigate?.(); }}
                    style={{ background: 'transparent', borderInlineEnd: 'none' }}
                />
            </div>
            <div className='shrink-0 border-t border-[var(--bg-white)]/10 p-2'>
                <button
                    onClick={handleLogout}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg whitespace-nowrap hover:bg-[var(--bg-white)]/10 transition-colors ${collapsed ? 'justify-center' : ''}`}
                >
                    <HiLogout size={18} /> {!collapsed && 'Log Out'}
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
